import { UseFormReset, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { z } from "zod";
import {
  SubscriptionFormData,
  FilterOptionSchema,
  type MetricDefinition,
  type SubscriptionV2Template,
} from "@/pages/subscriptions/services/types";
import { WizardStepEnum, PreviewItemType, SubscriptionType } from "@/pages/subscriptions/constants";

// Re-export WizardStepEnum as WizardStep for backwards compatibility
export type WizardStep = `${WizardStepEnum}`;

// ============================================================================
// Subscription Preview Types
// ============================================================================

export const PreviewChartDataSchema = z.object({
  type: z.string(),
  data: z.array(z.record(z.unknown())),
  x_key: z.string(),
  y_key: z.string(),
  title: z.string().optional(),
  x_label: z.string().optional(),
  y_label: z.string().optional(),
});

export const PreviewItemSchema = z.object({
  type: z.nativeEnum(PreviewItemType),
  content: z.string().optional().nullable(),
  chart: PreviewChartDataSchema.optional().nullable(),
});

export const SubscriptionPreviewResponseSchema = z.object({
  items: z.array(PreviewItemSchema),
  generated_at: z.string(),
});

export type PreviewChartData = z.infer<typeof PreviewChartDataSchema>;
export type PreviewItem = z.infer<typeof PreviewItemSchema>;
export type SubscriptionPreviewResponse = z.infer<typeof SubscriptionPreviewResponseSchema>;

export interface SubscriptionPreviewParams {
  rule_type: SubscriptionType;
  name?: string;
  metric_config?: {
    metric_name: string;
    category?: string;
    params?: Record<string, unknown>;
    filters?: Array<{ key: string; value: string[] }>;
  };
}

// ============================================================================
// Metric Filter Options Types
// ============================================================================

export const MetricFilterOptionsResponseSchema = z.object({
  filter_name: z.string(),
  options: z.array(FilterOptionSchema),
  total: z.number(),
  page: z.number().optional(),
  size: z.number().optional(),
});

export type MetricFilterOptionsResponse = z.infer<typeof MetricFilterOptionsResponseSchema>;

export interface UseSubscriptionFormDataProps {
  subscriptionId?: string;
  templateData?: SubscriptionV2Template;
  isEditMode: boolean;
  setValue: UseFormSetValue<SubscriptionFormData>;
  reset: UseFormReset<SubscriptionFormData>;
  watch: UseFormWatch<SubscriptionFormData>;
  setCurrentStep: (step: WizardStep) => void;
}

export interface GroupedBySource {
  [sourceName: string]: {
    source_display_label: string;
    categories: Record<string, MetricDefinition[]>;
  };
}

export interface UseSubscriptionFormDataReturn {
  subscriptionLoading: boolean;
  metricsLoading: boolean;
  metricsData: MetricDefinition[] | undefined;
  categoryDisplayNameLookup: Record<string, string>;
  metricDisplayNameLookup: Record<string, string>;
  categoryToSourceLookup: Record<string, string>;
  groupedBySource: GroupedBySource;
  selectedMetricDefinition: MetricDefinition | null;
}
